import { useEffect, useRef } from 'react';
import { EVENTO_TELAS_PRONTAS } from './atualizacaoPronta';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { getApiBase } from '@/config';
import { foraDaProva } from '@/features/relatos/podeRelatar';
import chave from './chaveDasTelas.json';
import { decidir, type Oferta, type Plataforma } from './decisaoDasTelas';
import { EVENTO_DO_CANAL, anotarDownload, anotarPendente, apagarDecisao, lerCanal, lerDecisao, lerDownload, lerPendente, registrarFalha, versoesQueFalharam } from './canalDoAparelho';
import { ABERTURA_ATUAL, estadoNoPlugin, oQueFazer } from './trocaDasTelas';
import { versaoDasTelasEmUso } from './versaoDasTelas';
import { provaDeVida } from './provaDeVida';

/**
 * O ATUALIZADOR DAS TELAS, no aplicativo (1.4). Montado uma vez, dentro do roteador. No navegador não faz nada.
 *
 * 1. `notifyAppReady` — o "subiu" que o plugin espera — só depois da PROVA DE VIDA do atualizador (`provaDeVida.ts`: ler o
 *    canal e completar uma consulta ao servidor). Se um pacote novo não chegar até aí em `appReadyTimeout`
 *    (capacitor.config.ts, 20 s), o plugin volta sozinho ao pacote anterior.
 * 2. Fora da prova, pergunta ao servidor — na primeira tela da abertura, ao voltar do segundo plano (`appStateChange`,
 *    abaixo; no máximo a cada 30 min) e NA HORA em que o canal muda em Minha conta (`EVENTO_DO_CANAL`, sem esperar os
 *    30 min) —, decide (`decisaoDasTelas.ts`), baixa com o SHA-256 e anota o pacote como pendente. A TROCA é da abertura
 *    seguinte, ANTES de desenhar login ou lista, com "Atualizando…" (`trocaNaAbertura.ts`, em `main.tsx`), e nunca com
 *    prova guardada no aparelho. O que está guardado (rascunhos, entrega pendente) fica: o pacote novo roda na mesma origem.
 */
const INTERVALO_MS = 30 * 60 * 1000;
/** As versões sendo baixadas NESTA abertura (memória): a anotação nativa cobre as aberturas; esta, as consultas simultâneas. */
const baixandoAgora = new Set<string>();

export function AtualizadorDeTelas() {
  const { pathname } = useLocation();
  const ultima = useRef(0);
  const pronto = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || pronto.current) return;
    pronto.current = true;
    void (async () => {
      try {
        const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
        // O "subiu" só depois da PROVA DE VIDA do atualizador (`provaDeVida.ts`): ler o canal guardado e completar uma
        // consulta ao servidor (resposta, erro de rede ou o teto). Pacote que trava antes disso é desfeito pelo plugin.
        // Roda em qualquer página, a da prova inclusive (só consulta; não baixa nem troca telas).
        await provaDeVida({
          lerCanal,
          consultar: async (canal, sinal) => {
            const plataforma = Capacitor.getPlatform();
            const build = Number((await App.getInfo()).build);
            const telas = encodeURIComponent(await versaoDasTelasEmUso());
            // A decisão da abertura (`trocaNaAbertura.ts`) vai junto, para a linha `[aplicativo]` do servidor; sai do
            // aparelho só depois de o servidor responder (sem internet, espera a próxima consulta).
            const decisao = await lerDecisao();
            const r = await fetch(`${getApiBase()}/api/aplicativo/telas/${canal}?plataforma=${plataforma}&build=${build}&telas=${telas}&prova=1${decisao ? `&abertura=${encodeURIComponent(decisao)}` : ''}`, { signal: sinal, cache: 'no-store' });
            if (decisao) await apagarDecisao();
            return r;
          },
          notificar: () => CapacitorUpdater.notifyAppReady(),
        });
        // O pacote que não subiu na abertura anterior (o plugin já voltou ao anterior e o apagou): guardar a versão.
        const falha = await CapacitorUpdater.getFailedUpdate().catch(() => null);
        if (falha?.bundle?.version) await registrarFalha(falha.bundle.version);
      } catch { /* sem o plugin (aplicativo antigo): nada a confirmar */ }
    })();
  }, []);

  // A tela atual, para as consultas que não nascem de uma troca de tela (a volta do segundo plano, o canal que mudou).
  const telaAtual = useRef(pathname);
  telaAtual.current = pathname;
  const consultar = useRef((forcar: boolean) => {
    if (!Capacitor.isNativePlatform() || !foraDaProva(telaAtual.current)) return;
    if (!forcar && Date.now() - ultima.current < INTERVALO_MS) return;
    ultima.current = Date.now();
    void verificar().catch(() => undefined);
  });

  useEffect(() => { consultar.current(false); }, [pathname]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const aoMudarOCanal = () => consultar.current(true);
    window.addEventListener(EVENTO_DO_CANAL, aoMudarOCanal);
    const volta = App.addListener('appStateChange', ({ isActive }) => { if (isActive) consultar.current(false); });
    return () => {
      window.removeEventListener(EVENTO_DO_CANAL, aoMudarOCanal);
      void volta.then((l) => l.remove());
    };
  }, []);

  return null;
}

async function verificar(): Promise<void> {
  const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
  const plataforma = Capacitor.getPlatform() as Plataforma;
  const info = await App.getInfo();
  const build = Number(info.build);
  const canal = await lerCanal();
  // A versão em uso: nula nas telas que vieram dentro do aplicativo (`versaoDasTelas.ts` — reconhecidas pelo `id`).
  const emUso = await versaoDasTelasEmUso();
  const versaoAtual = emUso === 'builtin' ? null : emUso;
  const ctrl = new AbortController();
  const tempo = setTimeout(() => ctrl.abort(), 15_000);
  let oferta: Oferta;
  try {
    // `telas`: a versão em uso, para a linha de log do servidor (`registrarPedidoDeTelas`, 20/10/2026).
    const r = await fetch(`${getApiBase()}/api/aplicativo/telas/${canal}?plataforma=${plataforma}&build=${build}&telas=${encodeURIComponent(versaoAtual ?? 'builtin')}`, { signal: ctrl.signal, cache: 'no-store' });
    if (!r.ok) return;
    oferta = await r.json();
  } finally { clearTimeout(tempo); }
  const d = await decidir(oferta, { plataforma, build, versaoAtual }, chave as JsonWebKey);
  if (!d.baixar) return;
  const lista = await CapacitorUpdater.list();
  // Uma versão que já FALHOU neste aparelho (não avisou "pronto" e o plugin voltou à anterior) não é baixada de novo: sem
  // isto, o pacote quebrado voltava a cada abertura. Só um pacote novo (outra versão) tira o aparelho daí.
  if (lista.bundles.some((b) => b.version === d.manifesto.versao && b.status === 'error')) return;
  if ((await versoesQueFalharam()).includes(d.manifesto.versao)) return; // a que falhou e o plugin já apagou
  // O ATUALIZADOR SÓ BAIXA E ANOTA (`trocaDasTelas.ts`); quem aplica é a PRÓXIMA ABERTURA, antes de desenhar a tela
  // (`trocaNaAbertura.ts`, em `main.tsx`). Sem `next` e sem condição de atraso — rearmar o `setMultiDelay` a cada abertura
  // impedia o plugin de instalar (20/10/2026).
  const versao = d.manifesto.versao;
  const doPlugin = lista.bundles.find((b) => b.version === versao && b.status !== 'error');
  const decisao = oQueFazer({ versao, estado: estadoNoPlugin(doPlugin?.status), pendente: await lerPendente(), download: await lerDownload(), aberturaAtual: ABERTURA_ATUAL });
  if (decisao === 'baixar') {
    if (baixandoAgora.has(versao)) return; // duas consultas ao mesmo tempo nesta abertura (a da abertura e a do canal)
    baixandoAgora.add(versao);
    try {
      // Anotado ANTES de começar: um segundo pedido nesta abertura vê a anotação e não começa outro.
      await anotarDownload({ versao, abertura: ABERTURA_ATUAL });
      await CapacitorUpdater.download({
        url: `${getApiBase()}/api/aplicativo/telas/${canal}/${d.manifesto.arquivo}?plataforma=${plataforma}&build=${build}&telas=${encodeURIComponent(versaoAtual ?? 'builtin')}`,
        version: versao,
        checksum: d.manifesto.sha256,
      });
      await anotarPendente({ versao, abertura: ABERTURA_ATUAL });
      await anotarDownload(null);
      window.dispatchEvent(new Event(EVENTO_TELAS_PRONTAS)); // a equipe vê "Atualização pronta · Aplicar agora"
    } finally { baixandoAgora.delete(versao); }
  } else if (decisao === 'anotar') {
    await anotarPendente({ versao, abertura: ABERTURA_ATUAL });
    window.dispatchEvent(new Event(EVENTO_TELAS_PRONTAS));
  }
}
